import { LoggingFacade } from '@vwdfive/myvw-logging';
import { DevicePlatform, Role } from '../enums';
import { IVumFlattenedVehicle } from '../vum/VumFlattenedVehicleConverter';
import { IVcfSubscriptionsSettingsTopic, VcfSubscriptionsSettingsClient } from './VcfSubscriptionsSettingsClient';
import { NoSubscriptionSettingsFoundError } from './errors/NoSubscriptionSettingsFoundError';

export interface ITheftAlarmVehicle extends IWeConnectVehicle {
    email: boolean;
    push: boolean;
    requestFailed?: boolean;
}

export interface IWeConnectVehicle extends IVumFlattenedVehicle {
    readonly vin: string;
}

export class VcfSubscriptionsSettingsService {
    constructor(private readonly vcfSubscriptionsSettingsClient: VcfSubscriptionsSettingsClient, private loggingFacade: LoggingFacade) {
    }

    public async retainTheftAlarmCapableVehicles(vehicles: IWeConnectVehicle[]): Promise<ITheftAlarmVehicle[]> {
        const filterUndefinedVehicles = (vehicle: ITheftAlarmVehicle | undefined) => vehicle !== undefined;
        const sortRequestSuccessVehiclesFirst = (vehicle?: ITheftAlarmVehicle) => vehicle?.requestFailed ?? false ? 1 : -1;
        const sortPrimaryVehicleFirst = (vehicle?: ITheftAlarmVehicle) => vehicle?.primaryCar && !!vehicle?.requestFailed ? -1 : 1;

        const vehiclesWithoutGuestUsers = vehicles.filter(vehicle => vehicle.role !== Role.GUEST_USER);
        const theftAlarmVehicles = await Promise.all(vehiclesWithoutGuestUsers.map((vehicle) => this.getTheftAlarmSettings(vehicle)));

        return theftAlarmVehicles
            .filter(filterUndefinedVehicles)
            .sort(sortPrimaryVehicleFirst)
            .sort(sortRequestSuccessVehiclesFirst) as ITheftAlarmVehicle[];
    }

    public async saveTheftAlarmSettings(vehicle: ITheftAlarmVehicle): Promise<void> {
        const isWcar = vehicle.devicePlatform === DevicePlatform.WCAR;
        const settings: IVcfSubscriptionsSettingsTopic[] = [{id: 'theftalarm', eMail: vehicle.email, push: vehicle.push}];
        await this.vcfSubscriptionsSettingsClient.saveSubscriptionsSettings(vehicle.vin, isWcar, settings);
    }

    private async getTheftAlarmSettings(vehicle: IWeConnectVehicle): Promise<ITheftAlarmVehicle | undefined> {
        const isWcar = vehicle.devicePlatform === DevicePlatform.WCAR;
        try {
            const settings = await this.vcfSubscriptionsSettingsClient.getSubscriptionsSettings(vehicle.vin, isWcar);
            const settingsWithTheftAlarmTopic = settings?.data?.topics?.find(topic => topic.id === 'theftalarm');
            if (settingsWithTheftAlarmTopic !== undefined) {
                return {
                    ...vehicle,
                    email: settingsWithTheftAlarmTopic.eMail ?? false,
                    push: settingsWithTheftAlarmTopic.push ?? false
                };
            }
        } catch (e: any) {
            if (!(e instanceof NoSubscriptionSettingsFoundError)) {
                this.loggingFacade.error(`Error fetching subscriptions settings for vin: ${vehicle.vin}`, e);
                return {
                    ...vehicle,
                    email: false,
                    push: false,
                    requestFailed: true
                };
            }
        }
        return undefined;
    }
}